import { rest } from "msw";
import tokenData from "./data/tokens";
import {
  MswChain,
  MswChainRaw,
  MswExpiryOffset,
  MswGasFee,
  MswPosition,
  MswQuoteResponse,
  MswQuoteSearchParams,
  MswTokenMetadata,
  parseFetchTokens,
} from "./data/snapshot";
import { mswGetQuoteTokenFieldResponse, mswGetQuoteType, mswGetTokenSymbols } from "./utils";
import { getChainIdFromName } from "../utils/chainUtils";
import { Address } from "@wagmi/core";
import { values } from "lodash-es";
import { insufficientLiquidityError } from "./data/quote/errors";

const API_BASE_URL = "https://api-uat.bebop.xyz";
let tokens: MswTokenMetadata | undefined;

const mswGetTokensHandler = [
  rest.get(`${API_BASE_URL}/:chain/v2/tokens`, (req, res, ctx) => {
    const { chain } = req.params;
    let json;

    switch (chain) {
      case "arbitrum":
        json = tokenData.arbitrum;
        break;
      case "ethereum":
        json = tokenData.ethereum;
        break;
      case "polygon":
        json = tokenData.polygon;
        break;
      default:
        if (Array.isArray(chain)) {
          throw new TypeError("Chain param cannot be an array.");
        }
        throw new TypeError(
          `The ${chain.toString()} chain is either invalid or not yet declared in Mock Service Worker.`
        );
    }

    // @ts-expect-error JSON file is subset of MswTokenMetadataResponse type
    tokens = parseFetchTokens(json);
    return res(ctx.status(200), ctx.json(json));
  }),
];

const mswGetQuoteHandler = [
  rest.get(`${API_BASE_URL}/:chain/v2/quote`, (req, res, ctx) => {
    const { chain: chainRaw } = req.params;
    if (Array.isArray(chainRaw)) {
      throw new Error(`Chain cannot be of array type: ${JSON.stringify(chainRaw)}`);
    }
    const chain = chainRaw as MswChain;
    // @ts-expect-error MswChain type is a subset of Chain type
    if (!Object.values(MswChainRaw).includes(chain)) {
      throw new Error(`Chain is not supported: ${JSON.stringify(chainRaw)}`);
    }

    const searchParamsRaw = Array.from(req.url.searchParams.entries());
    const searchParams: MswQuoteSearchParams = Object.fromEntries(
      searchParamsRaw.map(([key, value]: Array<string>) => {
        switch (key) {
          case "buy_tokens":
          case "sell_tokens":
            return [key, value.split(",")];
          case "sell_amounts":
          case "buy_tokens_ratios":
            return [key, value.split(",").map(value => parseFloat(value))];
          default:
            return [key, value];
        }
      })
    );

    // Build quote response
    const type = mswGetQuoteType(searchParams);
    if (tokens == null) {
      return res(ctx.status(500), ctx.body("Msw error: Cannot retrieve token data"));
    }
    const expiry = Date.now() + MswExpiryOffset[chain];
    const bebopMakerAddress: Address = "0xAF0B0000f0210D0f421F0009C72406703B50506B";
    const bebopMakerNonce = 1683218678416;

    const sellTokenSymbols = mswGetTokenSymbols(searchParams.sell_tokens, tokens, chain);
    const buyTokenSymbols = mswGetTokenSymbols(searchParams.buy_tokens, tokens, chain);

    const sellQuoteTokenFieldResponse = mswGetQuoteTokenFieldResponse(
      MswPosition.Sell,
      type,
      chain,
      tokens,
      sellTokenSymbols,
      buyTokenSymbols,
      searchParams.sell_amounts
    );
    const totalUsdValue = values(sellQuoteTokenFieldResponse)
      .map(value => value.amountUsd)
      .reduce((prev, curr) => curr + prev, 0);
    if (totalUsdValue > 1_000_000) {
      return res(ctx.status(200), ctx.body(JSON.stringify(insufficientLiquidityError)));
    }

    const buyQuoteTokenFieldResponse = mswGetQuoteTokenFieldResponse(
      MswPosition.Buy,
      type,
      chain,
      tokens,
      buyTokenSymbols,
      sellTokenSymbols,
      searchParams.sell_amounts,
      "buy_tokens_ratios" in searchParams ? searchParams.buy_tokens_ratios : undefined
    );

    const response: MswQuoteResponse = {
      type,
      quoteid: `${type}-0000000000000000000001`,
      chainid: getChainIdFromName(chain),
      receiver: searchParams.receiver_address,
      expiry,
      buyTokens: buyQuoteTokenFieldResponse,
      sellTokens: sellQuoteTokenFieldResponse,
      gasFee: {
        [MswGasFee[chain].currency]: MswGasFee[chain].feeNative,
        usd: MswGasFee[chain].feeUsd,
      },
      toSign: {
        expiry,
        taker_address: searchParams.taker_address,
        maker_address: [bebopMakerAddress],
        maker_nonces: [bebopMakerNonce],
        taker_tokens: [searchParams.sell_tokens],
        maker_tokens: [searchParams.buy_tokens],
        taker_amounts: [Object.values(sellQuoteTokenFieldResponse).map(token => token.amount)],
        maker_amounts: [Object.values(buyQuoteTokenFieldResponse).map(token => token.amount)],
        receiver: searchParams.receiver_address,
      },
    };

    return res(ctx.status(200), ctx.json(response));
  }),
];

export const handlers = [...mswGetTokensHandler, ...mswGetQuoteHandler];
